'use client';
import Link from 'next/link';
import { useRouter } from 'next/router';

export default function SmartLink({ link, className = '', label, children, ...props }) {
	const router = useRouter();

	if (!link) return children || null;

	// Handle both string URLs and object format
	let href = typeof link === 'string' ? link : (link.url || link.href || '#');
	let isExternal = false;
	let isEmail = false;
	let isPhone = false;

	const ariaProps = label ? { 'aria-label': label } : {};

	// Detect link type
	if (href.startsWith('mailto:')) {
		isEmail = true;
	} else if (href.startsWith('tel:')) {
		isPhone = true;
	} else if (/^https?:\/\//.test(href)) {
		// Check if external or internal
		const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || '';
		isExternal = !href.startsWith(siteUrl);
		
		// Convert WordPress URL to Next.js route
		if (!isExternal && siteUrl) {
			href = href.replace(siteUrl, '').replace(/\/$/, '') || '/';
		}
	} else if (!href.startsWith('/') && !href.startsWith('#')) {
		// Relative URL, make it absolute
		href = '/' + href;
	}

	// Email or phone link
	if (isEmail || isPhone) {
		return (
			<a
				href={href}
				className={className}
				{...ariaProps}
				{...props}
			>
				{children}
			</a>
		);
	}

	// External link
	if (isExternal) {
		return (
			<a
				href={href}
				className={className}
				target="_blank"
				rel="noopener noreferrer"
				{...ariaProps}
				{...props}
			>
				{children}
			</a>
		);
	}

	// Hash-only link: smooth scroll without updating URL
	if (href.startsWith('#')) {
		const handleHashClick = (e) => {
			e.preventDefault();

			document.querySelectorAll('.state-menu-open').forEach(el => el.classList.remove('state-menu-open'));
			document.querySelectorAll('.state-search-open').forEach(el => el.classList.remove('state-search-open'));

			const targetId = href.slice(1);
			if (!targetId) return;

			const target = document.getElementById(targetId) || document.querySelector(`[name="${targetId}"]`);
			if (target) {
				target.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		};

		return (
			<span
				role="link"
				onClick={handleHashClick}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') handleHashClick(e);
				}}
				tabIndex={0}
				className={className}
				{...ariaProps}
				{...props}
			>
				{children}
			</span>
		);
	}

	// Internal link with transition
	const handleClick = (e) => {
		e.preventDefault();

		document.body.classList.remove('state-page-loaded');
		document.querySelectorAll('.state-menu-open').forEach(el => el.classList.remove('state-menu-open'));
		document.querySelectorAll('.state-search-open').forEach(el => el.classList.remove('state-search-open'));

		const [currentPath] = (router.asPath || '/').split('#');
		const [targetPath, targetHash] = href.split('#');

		if (targetHash && (targetPath === '' || targetPath === currentPath)) {
			const target = document.getElementById(targetHash) || document.querySelector(`[name="${targetHash}"]`);
			if (target) {
				target.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
			return;
		}

		setTimeout(() => {
			router.push(href);
		}, 500);
	};

	return (
		<a
			href={href}
			onClick={handleClick}
			className={className}
			{...ariaProps}
			{...props}
		>
			{children}
		</a>
	);
}
